import React, { useState, useEffect } from 'react';
import CryptoJS from 'crypto-js';
import api from '../api';
import "./estilodashuser.css";

const AdicionarLicenca = ({ userId }) => {
  const [conta, setConta] = useState('');
  const [chave, setChave] = useState('');
  const [tipo, setTipo] = useState('Demo'); // Valor padrão "Demo"

  const [licencas, setLicencas] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [planos, setPlanos] = useState([]);

  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState(''); // 'sucesso' | 'erro'
  const [salvando, setSalvando] = useState(false);      // evita clique duplo

  useEffect(() => {
    if (userId) {
      carregarDados();
    }
  }, [userId]);

  const carregarDados = async () => {
    try {
      const [respCliente, respLicencas, respPlanos] = await Promise.all([
        api.get(`/api/clientes/${userId}`),
        api.get('/api/licencas'),
        api.get('/api/planos-ativos'),
      ]);

      setCliente(respCliente.data);

      const todasLicencas = respLicencas.data || [];
      const licencasDoCliente = todasLicencas
        .filter((lic) => lic.id_cliente === userId)
        .map((lic) => ({
          ...lic,
          status: Number(lic.status),
        }));

      setLicencas(licencasDoCliente);
      setPlanos(respPlanos.data || []);
      // não mexo em mensagem aqui, para não apagar mensagens de sucesso/erro
    } catch (error) {
      console.error('Erro ao carregar dados para adicionar licença:', error);
      setMensagem('Erro ao carregar dados. Tente novamente mais tarde.');
      setTipoMensagem('erro');
    }
  };

  const obterResumoSlots = () => {
    if (!cliente) {
      return {
        planoNome: '—',
        slotsContratados: 0,
        licencasAtivas: 0,
        slotsDisponiveis: 0,
      };
    }

    const slotsContratados =
      cliente.slots_contratados != null
        ? Number(cliente.slots_contratados)
        : 2;

    const licAtivas = licencas.filter((l) => l.status === 1).length;

    const plano = planos.find((p) => p.id === cliente.plano_id);
    const planoNome = plano ? plano.nome : 'Plano padrão';

    const slotsDisponiveis = Math.max(0, slotsContratados - licAtivas);

    return {
      planoNome,
      slotsContratados,
      licencasAtivas: licAtivas,
      slotsDisponiveis,
    };
  };

  const { planoNome, slotsContratados, licencasAtivas, slotsDisponiveis } =
    obterResumoSlots();

  const handleContaChange = (event) => setConta(event.target.value);

  const handleGerarChave = () => {
    if (!conta) {
      setMensagem('Por favor, insira o número da conta.');
      setTipoMensagem('erro');
      return;
    }

    const numeroConta = parseInt(conta, 10);
    const resultado = numeroConta * 11 - 181818;
    const hash = CryptoJS.MD5(resultado.toString()).toString(CryptoJS.enc.Hex);
    setChave(hash);
    setMensagem('');
    setTipoMensagem('');
  };

  const handleSalvarLicenca = async () => {
    if (salvando) return; // evita clique duplo enquanto está salvando

    if (!conta || !chave) {
      setMensagem('Preencha todos os campos antes de salvar.');
      setTipoMensagem('erro');
      return;
    }

    if (!cliente) {
      setMensagem(
        'Não foi possível identificar o cliente. Recarregue a página e tente novamente.'
      );
      setTipoMensagem('erro');
      return;
    }

    // Recalcula slots a partir do estado mais recente
    const {
      slotsDisponiveis: slotsDispCalc,
      licencasAtivas: licAtivasCalc,
      slotsContratados: slotsContrCalc,
    } = obterResumoSlots();

    if (slotsDispCalc <= 0) {
      setMensagem(
        `Você já utilizou todos os seus slots de licença (${licAtivasCalc}/${slotsContrCalc}). ` +
          'Para adicionar mais licenças, entre em contato com o suporte.'
      );
      setTipoMensagem('erro');
      return;
    }

    const licencaExistente = licencas.find((licenca) => licenca.conta === conta);
    if (licencaExistente) {
      setMensagem('Erro: Já existe uma licença para esta conta.');
      setTipoMensagem('erro');
      return;
    }

    const novaLicenca = {
      clienteId: userId,
      conta,
      lkey: chave,
      tipo,
      status: 1, // Sempre ativa ao criar
    };

    try {
      setSalvando(true);

      await api.post('/api/licencas', novaLicenca);

      // Recarrega tudo do backend para atualizar slots e lista de licenças
      await carregarDados();

      resetFormFields();
      setMensagem(
        'Licença adicionada com sucesso! Já pode testar em sua conta MT5.'
      );
      setTipoMensagem('sucesso');
    } catch (error) {
      console.error('Erro ao salvar licença:', error);
      setMensagem('Erro ao salvar licença. Tente novamente.');
      setTipoMensagem('erro');
    } finally {
      setSalvando(false);
    }
  };

  const resetFormFields = () => {
    setConta('');
    setChave('');
    setTipo('Demo'); // Reseta para o valor padrão "Demo"
  };

  return (
    <div className="adicionar-licenca-container">
      <center>
        <h3>Adicionar Licença</h3>
        <p>
          Plano atual: <strong>{planoNome}</strong> — Slots contratados:{' '}
          <strong>{slotsContratados}</strong> | Licenças ativas:{' '}
          <strong>{licencasAtivas}</strong> | Slots disponíveis:{' '}
          <strong>{slotsDisponiveis}</strong>
        </p>
        <p>
          Digite o número de sua conta MT5, clique em <strong>Gerar Chave</strong>,
          selecione o tipo de licença (Demo ou Real), e clique em{' '}
          <strong>Salvar Licença</strong>.
        </p>
      </center>

      {mensagem && (
        <p
          style={{
            color: tipoMensagem === 'erro' ? 'red' : 'green',
            fontWeight: 'bold',
          }}
        >
          {mensagem}
        </p>
      )}

      <form className="adicionar-licenca-form">
        <label htmlFor="conta">Número da Conta:</label>
        <input
          type="text"
          id="conta"
          value={conta}
          onChange={handleContaChange}
          placeholder="Digite o número da conta"
        />

        <button
          type="button"
          onClick={handleGerarChave}
          className="botao-gerar-chave"
          disabled={salvando}
        >
          Gerar Chave
        </button>

        <label htmlFor="chave">Chave:</label>
        <input type="text" id="chave" value={chave} readOnly />

        <label htmlFor="tipo">Tipo de Licença:</label>
        <select
          id="tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="select-tipo"
          disabled={salvando}
        >
          <option value="Real">Real</option>
          <option value="Demo">Demo</option>
        </select>

        <button
          type="button"
          onClick={handleSalvarLicenca}
          className="botao-salvar-licenca"
          disabled={slotsDisponiveis <= 0 || salvando}
        >
          {salvando ? 'Salvando...' : 'Salvar Licença'}
        </button>
      </form>

      {licencas.length > 0 && (
        <div className="licencas-existentes">
          <h4>Minhas Licenças</h4>
          <table>
            <thead>
              <tr>
                <th>Número da Conta</th>
                <th>Chave</th>
                <th>Tipo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {licencas.map((licenca) => (
                <tr key={licenca.id}>
                  <td>{licenca.conta}</td>
                  <td>{licenca.lkey}</td>
                  <td>{licenca.tipo}</td>
                  <td>
                    {licenca.status === 1 || licenca.status === '1'
                      ? 'Ativa'
                      : 'Inativa'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdicionarLicenca;
